import * as React from "react";
import { merge, $, media } from "glamor";
import { theme } from "styles/theme";
import DragIcon from "material-ui/svg-icons/editor/drag-handle";
import IconButton from "material-ui/IconButton";
import IconMenu from "material-ui/IconMenu";
import MoreHorizIcon from "material-ui/svg-icons/navigation/more-horiz";
import MenuItem from "material-ui/MenuItem";
import { formatMessage } from "lib/globalize-util";
import { DATATEST } from "constants/integration";
import CartIcon from "components/icons/assortments/cart";
import { IconAlertCircle } from "components/icons/assortments/icon-alert-circle";
import _ from "lodash";
import { ColorSwatchCircles } from "./color-swatch-circles";
import { ICollectionSwatch } from "constants/color-swatches";
import Tooltip from "components/tooltip";
import { IShopViewSettings } from "components/product-collection/manage-shop-view/types";

const CARD_MARGIN = 12;
const IMAGE_HEIGHT = 342;
const IMAGE_WIDTH = 218;
const ITEM_HEIGHT = 570;
const ITEM_WIDTH = 218;
const ADDITIONAL_INFO_HEIGHT = 106;
const MARGIN_TOP = 6;
const MARGIN_BOTTOM = 36 + MARGIN_TOP;

interface IProductTileProps {
    isSortable: boolean;
    id: string; // could be product or catalog
    imageUrl: string; // fallback url should be provided
    secondaryImageUrl?: string;
    brandName?: string;
    primaryAttribute: string; // e.g. product name
    quantity?: number;
    secondaryAttribute?: string; // MSRP or maybe catalog name
    tertiaryAttributes?: string[];
    additionalColors?: ICollectionSwatch[]; // #hex; https://url; name
    colorSwatch?: any;
    onSelectItem?: (e: React.MouseEvent) => void; // e.g. hearting a prod or catalog
    selected?: boolean;
    onLinkItem: (e: React.MouseEvent) => void;
    index: number; // index in collection of products/linesheets
    onItemClick: () => void;
    style?: any; // only used in conjunction with react-virtualized
    hasAdditionalVariants: boolean;
    allowDuplicateProduct?: boolean;
    costPrice?: string;
    unsynced?: boolean;
    shopViewSettings?: IShopViewSettings;
}

let styles;
const getStyles = () => {
    if (!styles) {
        styles = merge(
            $(" .node", {
                margin: `${MARGIN_TOP}px auto 0`,
                background: theme.palette.canvasColor,
                width: `${ITEM_WIDTH}px`,
                height: `${ITEM_HEIGHT}px`,
                cursor: "pointer",
                transition: "all 0.2s ease-out",
                borderRadius: "5px 5px 0 0",
                position: "relative",
            }),
            $(" .node:hover", {
                borderRadius: "5px",
                boxShadow: "0 4px 16px 0 rgba(45, 45, 45, 0.16)",
            }),
            $(" .dragHandle", {
                position: "absolute",
                opacity: 0,
                transition: "all 0.2s ease-out",
                touchAction: "none",
                zIndex: 11,
                backgroundColor: "#fff",
                borderRadius: "50%",
                width: "24px",
                height: "24px",
            }),
            $(" .isTouchDevice & .node .dragHandle", {
                opacity: 1,
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
            }),
            $(" .node:hover .dragHandle", {
                opacity: 1,
            }),
            $(" .images", {
                margin: "0",
                height: `${IMAGE_HEIGHT}px`,
                position: "relative",
                width: "100%",
                overflow: "hidden",
            }),
            $(" .image", {
                height: `${IMAGE_HEIGHT}px`,
            }),
            $(" .primaryImage", {
                width: `${IMAGE_WIDTH}px`,
                height: `${IMAGE_HEIGHT}px`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center center",
                transition: "all 0.2s ease-out",
            }),
            $(" .secondaryImageUrl", {
                position: "absolute",
                top: 0,
                left: 0,
                opacity: 0,
                width: `${IMAGE_WIDTH}px`,
                height: `${IMAGE_HEIGHT}px`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center center",
                transition: "all 0.2s ease-out",
            }),
            $(" .images:hover .secondaryImageUrl", {
                opacity: 1,
            }),
            $(" .imageIcons", {
                alignItems: "center",
                background: "rgba(0,0,0,.5)",
                bottom: "70px",
                color: "white",
                display: "flex",
                fontSize: "14px",
                height: "40px",
                paddingLeft: "8px",
                position: "absolute",
                textAlign: "center",
                width: "100%",
                right: "0px",
            }),
            $(" .brokenImageWrapper", {
                position: "absolute",
                bottom: "10px",
                left: "4px",
            }),
            $(" .imageIcons svg", {
                height: "30px !important",
                padding: "1px",
                width: "30px !important",
            }),
            $(" .noImage", {
                height: `${IMAGE_HEIGHT}px`,
                background: theme.palette.lighterGrey,
            }),
            $(" .additionalInfo", {
                maxHeight: `${ADDITIONAL_INFO_HEIGHT}px`,
            }),
            $(" .additionalInfo > div", {
                fontSize: "10px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: "1.4",
                textAlign: "center",
                paddingTop: "8px",
            }),
            $(" .additionalInfoFields", {
                fontSize: "12px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: "1.4",
                textAlign: "center",
                paddingTop: "4px",
            }),
            $(" .priceLabel", {
                fontSize: "11px !important",
            }),
            $(" .additionalInfo .image", {
                height: "20px",
                width: "20px",
            }),
            $(" .additionalInfo .svg", {
                height: "20px",
                width: "20px",
            }),
            $(" .title", {
                height: "20px",
                lineHeight: "20px",
                fontSize: "14px",
                letterSpacing: "0.2px",
                fontWeight: "bold",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: theme.palette.darkerGrey,
                textAlign: "center",
            }),
            $(" .subtitle, .text, .subtext", {
                height: "20px",
                lineHeight: "20px",
                fontSize: "12px",
                letterSpacing: "0.1px",
                color: theme.palette.darkerGrey,
                textAlign: "center",
            }),
            $(" .subtitle", {
                color: theme.palette.darkGrey,
                display: "flex",
            }),
            $(" .subtitle > div", {
                position: "absolute",
                paddingLeft: "22px",
                overflow: "hidden",
                height: "20px",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "92px",
            }),
            $(" .groupHeader", {
                backgroundColor: theme.palette.lightestGrey,
                borderRadius: "5px",
                margin: "0 4px",
                height: "72px",
                overflow: "auto",
            }),
            $(" .groupHeader h3", {
                margin: "0",
                padding: "0 16px",
                fontSize: "28px",
                lineHeight: "72px",
            }),
            $(" .groupTitle", {
                paddingLeft: "20px",
                height: "36px",
                lineHeight: "36px",
                fontSize: "17px",
            }),
            $(" .groupStats", {
                display: "flex",
            }),
            $(" .groupStat", {
                padding: "0 20px",
            }),
            $(" .groupStatTitle", {
                fontWeight: "bold",
            }),
            $(" .widget", {
                position: "absolute",
                bottom: "0px",
                right: "4px",
                opacity: 0,
                transition: "all 0.2s ease-out",
                zIndex: 11,
            }),
            $(" .node:hover .widget", {
                opacity: 1,
            }),
            $(" .isTouchDevice & .node .widget", {
                opacity: 1,
                bottom: 10,
                right: "-20px",
                transform: "translateX(-50%)",
            }),
            $(" .attributeContainer", {
                display: "flex",
                minHeight: "30px",
            }),
            $(" .primaryAttribute", {
                fontSize: "14px",
                fontWeight: "bold",
                color: "#2d2d2d",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: "1.33",
                textAlign: "center",
                paddingTop: "8px",
            }),
            $(" .priceAttribute", {
                flexGrow: "1",
                height: "16px",
                fontSize: "14px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: "bold",
                lineHeight: "16px",
                textAlign: "center",
            }),
            $(" .costPrice", {
                marginBottom: "2px",
            }),
            $(" .innerScrollContainer", {
                padding: "10px",
            }),
            $(" .favorite", {
                position: "absolute !important",
                bottom: "0px",
                right: "0px",
                zIndex: 10,
                opacity: 0,
            }),
            media(
                "(hover: none)",
                $(" .favorite", {
                    opacity: 1,
                })
            ),
            $(".selected .favorite", {
                opacity: 1,
            }),
            $(".selected .favorite:disabled svg", {
                opacity: 0.5,
                fill: `${theme.palette.accent1Color} !important`,
            }),
            $(".selected .favorite:hover svg", {
                fill: `${theme.palette.accent1Color} !important`,
            }),
            $(":hover .favorite", {
                opacity: 1,
            }),
            $(" .favorite:hover svg", {
                fill: `${theme.palette.darkestGrey} !important`,
            }),
            $(" .favorite-left", {
                position: "absolute !important",
                bottom: "0px",
                left: "0px",
                zIndex: 10,
            }),
            $(" .colorCarousel", {
                height: "50px",
                display: "flex",
                justifyContent: "center",
                margin: "10px 0 0",
            }),
            $(" .brandAttribute", {
                width: `${IMAGE_WIDTH}px`,
                fontFamily: "Lato",
                fontSize: "10px",
                color: "#989898",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: "1.6",
                textAlign: "center",
                paddingTop: "12px",
            }),
            $(" .quantityContainer", {
                display: "flex",
                marginTop: "0px",
                paddingTop: "0 !important",
            }),
            $(" .quantityPill", {
                backgroundColor: "#585858",
                borderRadius: "12px",
                color: theme.palette.white,
                height: "16px",
                fontSize: "12px",
                lineHeight: "1.33",
                padding: "0 8px",
                textAlign: "center",
            }),
            $(" .colorSwatchContainer", {
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                position: "relative",
                border: `2px solid ${theme.palette.white}`,
            }),
            $(" .colorSwatchContainer.selected", {
                border: `2px solid ${theme.palette.swatchBorderColor}`,
            }),
            $(" .colorSwatch", {
                width: "22px",
                height: "22px",
                borderRadius: "50%",
                position: "absolute",
                top: "2px",
                left: "2px",
                border: `1px solid ${theme.palette.swatchBorderColor}`,
                backgroundSize: "22px !important",
            }),
            $(" .selectedColorSwatch", {
                height: "32px",
                width: "32px",
                flexShrink: 0,
                borderRadius: "12px",
                border: "solid 1px #c1c1c1",
                position: "absolute",
                top: "290px",
                right: "12px",
                backgroundSize: "32px !important",
            }),
            $(" .priceAttribute.rightOriented", {
                marginLeft: "auto",
                flexGrow: "0 !important",
            }),
            $(" .textContent", {
                height: "200px",
            }),
            $(" .cardFooter", {
                position: "absolute",
                bottom: 0,
                left: "8px",
                right: "8px",
                padding: "0 0 12px",
                textAlign: "center",
                borderBottom: "1px solid #cdcdcd",
                transition: "all 0.2s ease-out",
            }),
            $(" .node:hover .cardFooter", {
                borderBottom: "1px solid transparent",
            }),
            $(" .hasAdditionalVariants", {
                height: "30px",
                lineHeight: "30px",
                borderRadius: "4px",
                backgroundColor: theme.palette.lightestGrey,
                color: theme.palette.grey,
                textAlign: "center",
                fontSize: "12px",
                fontStyle: "italic",
                marginTop: "8px",
            })
        );
    }
    return styles;
};

export const ProductTile: React.FunctionComponent<IProductTileProps> = (props) => {
    const {
        isSortable,
        id,
        imageUrl,
        index,
        onSelectItem,
        onLinkItem,
        primaryAttribute,
        secondaryAttribute,
        tertiaryAttributes = [],
        selected,
        onItemClick,
        style,
        secondaryImageUrl,
        brandName,
        quantity,
        additionalColors = [],
        colorSwatch,
        hasAdditionalVariants,
        allowDuplicateProduct,
        costPrice,
        unsynced,
        shopViewSettings,
    } = props;

    const additionalAttributes = tertiaryAttributes.map((val, i) => (
        <div key={i} className="additionalInfoFields">
            {val}
        </div>
    ));

    const swatches = React.useMemo(() => {
        const sortedColors = _.sortBy(additionalColors, "selected").reverse();
        const _swatches = sortedColors.map((swatchProps, i) => {
            if (i < 5) {
                return <ColorSwatchCircles key={i} {...swatchProps} />;
            } else {
                return null;
            }
        });
        return _swatches;
    }, [additionalColors]);

    return (
        <div {...getStyles()} className={selected ? "selected" : undefined} style={style} onClick={onItemClick}>
            <div className="node">
                {isSortable && (
                    <div className="dragHandle" data-draghandle={true}>
                        <DragIcon color={theme.palette.grey} />
                    </div>
                )}
                {isSortable && (
                    <div className="widget">
                        <IconMenu
                            iconButtonElement={
                                <IconButton>
                                    <MoreHorizIcon color={theme.palette.darkGrey} data-stoppropagation={true} />
                                </IconButton>
                            }
                            anchorOrigin={{ horizontal: "right", vertical: "top" }}
                            targetOrigin={{ horizontal: "right", vertical: "top" }}
                            useLayerForClickAway={true}
                        >
                            {onSelectItem && (
                                <MenuItem
                                    primaryText={formatMessage("Remove Item")}
                                    data-index={index}
                                    data-id={id}
                                    onClick={onSelectItem}
                                />
                            )}
                            <MenuItem
                                primaryText={formatMessage("Link Product")}
                                data-index={index}
                                data-id={id}
                                onClick={onLinkItem}
                            />
                        </IconMenu>
                    </div>
                )}
                {imageUrl && (
                    <div className="images">
                        <div
                            className="primaryImage"
                            style={{
                                width: `${IMAGE_WIDTH}px`,
                                height: `${IMAGE_HEIGHT}px`,
                                backgroundImage: `url(${imageUrl})`,
                                backgroundSize: "contain",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "center center",
                            }}
                        />
                        {secondaryImageUrl && (
                            <div
                                className="secondaryImageUrl"
                                style={{
                                    width: `${IMAGE_WIDTH}px`,
                                    height: `${IMAGE_HEIGHT}px`,
                                    backgroundImage: `url(${secondaryImageUrl})`,
                                    backgroundSize: "contain",
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "center center",
                                    backgroundColor: "white",
                                }}
                            />
                        )}
                    </div>
                )}
                {!imageUrl && <div className="image" />}
                <div data-test={DATATEST.ASSORTMENTS.SHOP_CARD_DETAILS_CONTAINER} className="textContent">
                    {colorSwatch && colorSwatch.swatchValue && (
                        <div
                            className="selectedColorSwatch"
                            style={{
                                background: colorSwatch.swatchValue,
                            }}
                        />
                    )}
                    <div className="brandAttribute">{brandName}</div>
                    <div className="primaryAttribute">{primaryAttribute}</div>
                    {additionalAttributes}
                    {swatches.length > 1 && <div className="colorCarousel">{swatches}</div>}
                    {hasAdditionalVariants && (
                        <div className="hasAdditionalVariants">{formatMessage("More colors available")}</div>
                    )}
                    <div className="cardFooter">
                        {(costPrice || !shopViewSettings || shopViewSettings.showCost) && (
                            <>
                                <div className="priceLabel">{"Cost"}</div>
                                <div
                                    data-test={DATATEST.ASSORTMENTS.SHOP.SETTINGS.PRODUCT.COST}
                                    className="priceAttribute costPrice"
                                >
                                    {costPrice}
                                </div>
                            </>
                        )}
                        {(!shopViewSettings || shopViewSettings.showRetail) && (
                            <>
                                <div className="priceLabel">{"Retail"}</div>
                                <div className="quantityContainer">
                                    <div
                                        data-test={DATATEST.ASSORTMENTS.SHOP.SETTINGS.PRODUCT.RETAIL}
                                        className={`priceAttribute ${
                                            typeof quantity === "number" ? "rightOriented" : ""
                                        }`}
                                    >
                                        {secondaryAttribute}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    {unsynced && (
                        <Tooltip
                            title={formatMessage("Not yet synced. Additional data and imagery is not yet available.")}
                            placement="top"
                        >
                            <IconButton data-test={DATATEST.ASSORTMENTS.SHOP_UNSYNCED_BTN} className="favorite-left">
                                <IconAlertCircle color={theme.palette.darkGrey} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {onSelectItem && (
                        <IconButton
                            data-test={DATATEST.ASSORTMENTS.SHOP_CART_BTN}
                            className="favorite"
                            onClick={onSelectItem}
                            disabled={selected && !allowDuplicateProduct}
                        >
                            <CartIcon
                                color={
                                    selected ? theme.palette.accent1Color : theme.palette.darkGrey
                                } /* fillInBasket={selected} */
                            />
                        </IconButton>
                    )}
                </div>
            </div>
        </div>
    );
};

(ProductTile as any).height = ITEM_HEIGHT;
(ProductTile as any).width = ITEM_WIDTH + CARD_MARGIN * 2;
(ProductTile as any).horizontalOffset = 0;
(ProductTile as any).verticalOffset = MARGIN_BOTTOM;
